import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CustomForm } from '@urbantxt/form';
import { ParagraphText } from '@urbantxt/paragraph-text';
import { Button } from '@urbantxt/button';
import { Checkbox } from 'rsuite';
import { setPostLoginRedirectFromLocation } from 'utils/postLoginRedirect';
import useBagelSmsConsent, {
  CONSENT_LABEL,
} from '../../hooks/useBagelSmsConsent';
import styles from './BagelSmsConsentForm.module.scss';

/**
 * Public opt-in for the Bagel AI SMS pilot. The page itself is reachable with
 * no login (Twilio A2P review needs to load it and see the consent mechanism
 * described), but actually recording consent requires being logged in — an
 * anonymous visitor sees only the consent context + a "Login to Confirm"
 * button, which stashes this path as the post-login redirect target (same
 * mechanism RedirectToLoginPreservingNext already uses) so they land right
 * back here after signing in.
 *
 * The consent checkbox is rendered manually rather than via CustomForm's
 * `checkbox` field type — see useBagelSmsConsent.ts for why that path crashes.
 */
function BagelSmsConsentForm() {
  const {
    fields,
    formValue,
    isLoading,
    isSubmitted,
    isLoggedIn,
    model,
    onChange,
    onSubmit,
    setConsent,
  } = useBagelSmsConsent();
  const location = useLocation();
  const navigate = useNavigate();

  const isConsented = formValue.consent.includes('consent');

  const handleLoginToConfirm = () => {
    setPostLoginRedirectFromLocation(location.pathname, location.search, location.hash);
    navigate('/');
  };

  if (isSubmitted) {
    return (
      <div className={styles.container}>
        <ParagraphText>
          <strong>Thanks!</strong>
        </ParagraphText>
        <ParagraphText marginTop={10}>Your consent has been recorded.</ParagraphText>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.headerContainer}>
        <ParagraphText>
          <strong>Bagel AI Text Messaging</strong>
        </ParagraphText>
        <ParagraphText marginTop={10}>
          {isLoggedIn
            ? 'Confirm or update your mobile number, then agree below to opt in to text messages from Bagel AI.'
            : 'Log in with your URBAN TXT staff account to opt in to text messages from Bagel AI.'}
        </ParagraphText>
      </div>

      {isLoggedIn && (
        <CustomForm
          onChange={onChange}
          onSubmit={onSubmit}
          model={model}
          formValue={formValue}
          isLoading={isLoading}
          fields={fields}
          isDisabled={false}
          hasChanges
        />
      )}

      <div className={styles.consentRow}>
        <Checkbox checked={isConsented} onChange={(_v, checked) => setConsent(checked)}>
          {CONSENT_LABEL}
        </Checkbox>
      </div>

      {isLoggedIn ? (
        <Button onClick={onSubmit} loading={isLoading} disabled={isLoading} size="full" label="Confirm" />
      ) : (
        <Button onClick={handleLoginToConfirm} size="full" label="Login to Confirm" />
      )}
    </div>
  );
}

export default BagelSmsConsentForm;
